import dayjs from 'dayjs';
import 'dayjs/locale/es.js';
import { formatCurrency } from './currency.ts';
import { isClientInRuleCategory } from './segmentRuleInference.ts';
import type { CrmClient, CrmSegment, CrmSubFilter, SegmentMemberships } from '@/types/crm';
import type { Settings } from '@/types/settings';

dayjs.locale('es');

export function getInitials(name?: string): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface CrmBadgeItem {
  key: string;
  label: string;
  type: 'birthday' | 'vip' | 'top_spender' | 'new' | 'lapsed' | 'debt' | 'upcoming' | 'general';
  variant: 'pink' | 'amber' | 'purple' | 'blue' | 'slate' | 'red' | 'neutral';
  iconName?: 'cake' | 'star' | 'sparkles' | 'user' | 'clock' | 'alert' | 'calendar';
}

/**
 * Calculates days until next birthday and generates a contextual label.
 * Returns null if day or month is missing/invalid.
 */
export function formatBirthdayInfo(
  day?: number | null,
  month?: number | null
): { isBirthdayToday: boolean; daysUntil: number | null; label: string } | null {
  if (!day || !month) return null;
  const now = dayjs().startOf('day');
  const currentYear = now.year();

  let bdayThisYear = dayjs(
    `${currentYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  ).startOf('day');

  if (!bdayThisYear.isValid()) return null;

  if (bdayThisYear.isBefore(now, 'day')) {
    bdayThisYear = bdayThisYear.add(1, 'year');
  }

  const diffDays = bdayThisYear.diff(now, 'day');
  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const dateStr = `${day} ${monthNames[month - 1] || ''}`;

  if (diffDays === 0) {
    return { isBirthdayToday: true, daysUntil: 0, label: `Hoy es su cumpleaños (${dateStr})` };
  }
  if (diffDays === 1) {
    return { isBirthdayToday: false, daysUntil: 1, label: `Cumpleaños mañana (${dateStr})` };
  }
  if (diffDays <= 30) {
    return { isBirthdayToday: false, daysUntil: diffDays, label: `Cumple en ${diffDays} días (${dateStr})` };
  }

  return { isBirthdayToday: false, daysUntil: diffDays, label: `Cumpleaños: ${dateStr}` };
}

/**
 * Computes client tenure and returns a friendly string (e.g. "Cliente desde hace 2 años (Feb 2024)").
 */
export function formatClientTenure(createdAt?: string): string {
  if (!createdAt) return '';
  const created = dayjs(createdAt);
  if (!created.isValid()) return '';

  const now = dayjs();
  const months = now.diff(created, 'month');
  const years = now.diff(created, 'year');
  const formattedDate = created.format('DD/MM/YY');

  if (years >= 1) {
    return `Cliente desde hace ${years} ${years === 1 ? 'año' : 'años'} (${formattedDate})`;
  }
  if (months >= 1) {
    return `Cliente desde hace ${months} ${months === 1 ? 'mes' : 'meses'} (${formattedDate})`;
  }
  const days = now.diff(created, 'day');
  return days <= 0 ? `Registrado hoy (${formattedDate})` : `Registrado hace ${days} ${days === 1 ? 'día' : 'días'} (${formattedDate})`;
}

/**
 * Resolves multiple contextual badges for a CRM client simultaneously.
 */
export function getCrmClientBadges(
  client: CrmClient,
  _activeSegmentId?: string,
  daysAbsent: number = 0,
  memberships?: SegmentMemberships,
  settings?: Settings,
  segments: CrmSegment[] = []
): CrmBadgeItem[] {
  const badges: CrmBadgeItem[] = [];

  // 1. Debt badge
  if ((client.outstandingAmount || 0) > 0.01) {
    badges.push({
      key: 'debt',
      label: `Deuda: ${formatCurrency(client.outstandingAmount, settings?.currencyCode || 'USD')}`,
      type: 'debt',
      variant: 'red',
      iconName: 'alert',
    });
  }

  // 2. Upcoming Appointment badge (Con próxima cita)
  const hasUpcoming = Boolean(
    client.nextAppointmentDate ||
    isClientInRuleCategory(client, 'upcoming', segments, memberships)
  );

  if (hasUpcoming) {
    badges.push({
      key: 'upcoming',
      label: 'Con próxima cita',
      type: 'upcoming',
      variant: 'blue',
      iconName: 'calendar',
    });
  }

  // 3. Birthday badge
  const bday = formatBirthdayInfo(client.birthdayDay, client.birthdayMonth);
  const isBirthday =
    isClientInRuleCategory(client, 'birthday', segments, memberships) ||
    Boolean(bday && bday.daysUntil !== null && bday.daysUntil <= 30);

  if (isBirthday) {
    badges.push({
      key: 'birthday',
      label: bday?.label || 'Cumpleaños próximo',
      type: 'birthday',
      variant: 'pink',
      iconName: 'cake',
    });
  }

  // 4. VIP / Loyal badge
  const isVip = isClientInRuleCategory(client, 'vip', segments, memberships);

  if (isVip) {
    badges.push({
      key: 'vip',
      label: 'Cliente Fiel',
      type: 'vip',
      variant: 'amber',
      iconName: 'star',
    });
  }

  // 5. Top Spender badge
  const isTopSpender = isClientInRuleCategory(client, 'top_spender', segments, memberships);

  if (isTopSpender) {
    badges.push({
      key: 'top_spender',
      label: 'Mayor Consumo',
      type: 'top_spender',
      variant: 'purple',
      iconName: 'sparkles',
    });
  }

  // 6. New Client badge
  const isFirstVisit = isClientInRuleCategory(client, 'first_visit', segments, memberships);
  const isNew = isClientInRuleCategory(client, 'new', segments, memberships) || isFirstVisit;

  if (isNew) {
    badges.push({
      key: 'new',
      label: isFirstVisit ? '1ra Visita' : 'Nuevo',
      type: 'new',
      variant: 'blue',
      iconName: 'user',
    });
  }

  // 7. Lapsed / Inactive badge
  const isLapsed =
    !client.nextAppointmentDate &&
    (isClientInRuleCategory(client, 'lapsed', segments, memberships) ||
      daysAbsent >= (settings?.inactivityDays || 60));

  if (isLapsed) {
    badges.push({
      key: 'lapsed',
      label: daysAbsent > 0 ? `${daysAbsent}d ausente` : 'Inactivo',
      type: 'lapsed',
      variant: 'slate',
      iconName: 'clock',
    });
  }

  return badges;
}

/**
 * Formats original currency and appends converted Bolivares (Bs.) if a valid exchange rate is provided.
 */
export function formatAmountWithBs(
  amount: number,
  currencyCode: string,
  rate?: number | null
): { original: string; bs?: string } {
  const original = formatCurrency(amount, currencyCode);
  if (!rate || rate <= 0 || !amount || amount <= 0) {
    return { original };
  }
  const bsAmount = amount * rate;
  const formattedBs = bsAmount.toLocaleString('es-VE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return { original, bs: `Bs. ${formattedBs}` };
}

/**
 * Determines if a CRM client matches a given segment filter.
 */
export function isClientInSegment(
  client: CrmClient,
  segmentId: string,
  _settings?: Settings,
  memberships?: SegmentMemberships
): boolean {
  if (!segmentId || segmentId === 'all') return true; // '' is 'Todos los clientes'
  if (client.segmentId === segmentId) return true;
  if (memberships?.[segmentId]?.length) {
    return memberships[segmentId].includes(client.id);
  }
  return false;
}

/**
 * Determines if a CRM client matches a given cross-filter / subfilter.
 */
export function matchesCrmSubFilter(
  client: CrmClient,
  subFilter: CrmSubFilter,
  settings?: Settings,
  memberships?: SegmentMemberships,
  segments: CrmSegment[] = []
): boolean {
  if (!subFilter || subFilter === 'all') return true;

  switch (subFilter) {
    case 'vip':
      return isClientInRuleCategory(client, 'vip', segments, memberships);

    case 'top_spender':
      return isClientInRuleCategory(client, 'top_spender', segments, memberships);

    case 'lapsed':
      return !client.nextAppointmentDate && isClientInRuleCategory(client, 'lapsed', segments, memberships);

    case 'new':
      return (
        isClientInRuleCategory(client, 'new', segments, memberships) ||
        isClientInRuleCategory(client, 'first_visit', segments, memberships)
      );

    case 'birthday': {
      const bday = formatBirthdayInfo(client.birthdayDay, client.birthdayMonth);
      return (
        isClientInRuleCategory(client, 'birthday', segments, memberships) ||
        Boolean(bday && bday.daysUntil !== null && bday.daysUntil <= 30)
      );
    }

    case 'with_appointments':
      return Boolean(
        client.nextAppointmentDate ||
        isClientInRuleCategory(client, 'upcoming', segments, memberships)
      );

    case 'debt':
      return (client.outstandingAmount || 0) >= (settings?.debtorMinAmount || 0.01);

    default:
      return true;
  }
}


